import { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { notify } from "../../utils/notify";
import {
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowUpTrayIcon,
  FolderIcon,
  ListBulletIcon,
  ViewColumnsIcon,
  ChevronDoubleLeftIcon,
  EllipsisHorizontalIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowUpRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import {
  useDocuments,
  useDeleteDocument,
  useCollections,
  useCreateCollection,
  useToggleDocumentCollection,
  useBulkAddToCollection,
} from "../../features/documents/hooks";
import type { Document, DocumentStatus } from "../../types/documents";
import { MiddleTruncate } from "../../components/common/MiddleTruncate";
import {
  isFileDragEvent,
  stashDroppedFiles,
} from "../../features/documents/fileDrop";
import { usePreventWindowFileDrop } from "../../features/documents/hooks/usePreventWindowFileDrop";

// Collection swatches. Olive (brand accent) leads as the default; the cool
// options are muted so nothing reads as a stark blue/purple against the warm UI.
const PRESET_COLORS = [
  "#5C6638", // olive (brand default)
  "#2E7D4F", // green
  "#C99A33", // gold
  "#B0492E", // terracotta
  "#4F6D7A", // muted slate
  "#7E6B8F", // muted mauve
];

const UNCATEGORISED = "__uncat__";

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatTotalSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

/** Grip handle drawn to match the mock (two columns of dots). */
function GripIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="text-[var(--text-faint)] opacity-50"
    >
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </svg>
  );
}

/** Token status pill with a leading dot, matching the mock. */
function statusKind(status: DocumentStatus): "ok" | "warn" | "err" | "muted" {
  switch (status) {
    case "INDEXED":
      return "ok";
    case "FAILED":
      return "err";
    case "ARCHIVED":
      return "muted";
    default:
      return "warn"; // PRESIGNED / QUEUED / PROCESSING / PROCESSED / PARTIALLY_INDEXED
  }
}

function StatusPill({ status }: { status: DocumentStatus }) {
  const kind = statusKind(status);
  const styles: Record<string, { wrap: string; dot: string }> = {
    ok: {
      wrap: "bg-[var(--ok-bg)] text-[var(--ok-text)]",
      dot: "bg-[var(--ok-dot)]",
    },
    warn: {
      wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
      dot: "bg-[var(--warn-dot)]",
    },
    err: {
      wrap: "bg-[var(--err-bg)] text-[var(--err-text)]",
      dot: "bg-[var(--err-dot)]",
    },
    muted: {
      wrap: "bg-[var(--surface-2)] text-[var(--text-3)]",
      dot: "bg-[var(--text-faint)]",
    },
  };
  const s = styles[kind];
  return (
    <span
      className={`inline-flex w-fit items-center gap-1.5 px-[9px] py-[3px] rounded-full text-[11px] font-semibold tracking-[0.03em] ${s.wrap}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

function CreateCollectionModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string>(PRESET_COLORS[0]);
  const { createCollection, isCreating, error } = useCreateCollection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await createCollection({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
      });
      onClose();
    } catch {
      // error is set in hook
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-[var(--surface)] rounded-[14px] border border-[var(--border)] shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--text)]">
              New Collection
            </h3>
            <button
              onClick={onClose}
              className="text-[var(--text-faint)] hover:text-[var(--text-2)]"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-2)] mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text)] text-sm focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Collection name"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-2)] mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text)] text-sm focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-2)] mb-1">
                Color
              </label>
              <div className="flex gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === c
                        ? "border-[var(--text)] scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-[var(--err-text)]">{error}</p>}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[var(--text-2)] bg-[var(--surface)] border border-[var(--border)] rounded-lg hover:bg-[var(--surface-2)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || !name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

type CollectionLite = { id: string; name: string; color: string | null };

/** Row action menu (⋯) — open, add/remove collection, delete. */
function RowMenu({
  doc,
  collections,
  onOpen,
  onAdd,
  onRemove,
  onDelete,
  onClose,
}: {
  doc: Document;
  collections: CollectionLite[];
  onOpen: () => void;
  onAdd: (collectionId: string) => void;
  onRemove: (collectionId: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const inIds = new Set((doc.collections || []).map((c) => c.id));
  const notIn = collections.filter((c) => !inIds.has(c.id));
  const currentlyIn = collections.filter((c) => inIds.has(c.id));

  return (
    <div
      ref={ref}
      className="absolute right-2 top-9 z-30 w-56 rounded-[10px] border border-[var(--border)] bg-[var(--surface)] shadow-xl py-1 text-[13px]"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onOpen}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)]"
      >
        <ArrowUpRightIcon className="h-4 w-4" />
        Open
      </button>

      {notIn.length > 0 && (
        <>
          <div className="mt-1 border-t border-[var(--border-2)] px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)]">
            Add to collection
          </div>
          {notIn.map((c) => (
            <button
              key={c.id}
              onClick={() => onAdd(c.id)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)]"
            >
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: c.color || "var(--accent)" }}
              />
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </>
      )}

      {currentlyIn.length > 0 && (
        <>
          <div className="mt-1 border-t border-[var(--border-2)] px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)]">
            Remove from
          </div>
          {currentlyIn.map((c) => (
            <button
              key={c.id}
              onClick={() => onRemove(c.id)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)]"
            >
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: c.color || "var(--accent)" }}
              />
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </>
      )}

      <div className="mt-1 border-t border-[var(--border-2)] pt-1">
        <button
          onClick={onDelete}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-[var(--err-text)] hover:bg-[var(--err-bg)]"
        >
          <TrashIcon className="h-4 w-4" />
          Delete
        </button>
      </div>
    </div>
  );
}

type SortKey = "name" | "status" | "size" | "added";
type SortDir = "asc" | "desc";

const SORT_COLUMNS: Array<[SortKey, string]> = [
  ["name", "File name"],
  ["status", "Status"],
  ["size", "Size"],
  ["added", "Added"],
];

// First click on a column picks the direction people usually want
const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  status: "asc",
  size: "desc",
  added: "desc",
};

// Pipeline order, so "asc" reads as least → most progressed
const STATUS_RANK: Record<DocumentStatus, number> = {
  PRESIGNED: 0,
  QUEUED: 1,
  PROCESSING: 2,
  PROCESSED: 3,
  PARTIALLY_INDEXED: 4,
  INDEXED: 5,
  FAILED: 6,
  ARCHIVED: 7,
};

const compareName = (a: Document, b: Document) =>
  a.filename.localeCompare(b.filename, undefined, {
    sensitivity: "base",
    numeric: true,
  });

const SORT_COMPARATORS: Record<SortKey, (a: Document, b: Document) => number> =
  {
    name: compareName,
    status: (a, b) =>
      (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99),
    size: (a, b) => (a.sizeBytes || 0) - (b.sizeBytes || 0),
    added: (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  };

const ROW_GRID =
  "grid grid-cols-[26px_38px_1fr_118px_78px_92px_30px] items-center gap-2";

// Row checkboxes handle selection on click (onChange carries no modifier keys,
// and shift-click needs them); this keeps React's controlled-input warning quiet.
const noop = () => {};

export function DocumentListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("collection") || "all";

  const [colsOpen, setColsOpen] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Column sort (#182). Default = Added, newest first: makes the API's
  // implicit order explicit and always reachable from the header.
  const [sortKey, setSortKey] = useState<SortKey>("added");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(DEFAULT_SORT_DIR[key]);
    }
  };
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [menuDocId, setMenuDocId] = useState<string | null>(null);

  // Drag-and-drop state: which docs are being dragged (a ticked row drags the
  // whole selection, an unticked one just itself), which collection is hovered.
  const [draggedDocIds, setDraggedDocIds] = useState<Set<string> | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);

  // OS-file drag over the files pane. Depth counter because dragenter/leave
  // fire per child element; guarded so internal doc-row drags never tick it.
  const [fileDragDepth, setFileDragDepth] = useState(0);
  const fileDragActive = fileDragDepth > 0;
  // Also clears the highlight when a drag ends outside the pane (cancelled,
  // dropped elsewhere, or dragged out of the window) — those paths never
  // deliver the balancing dragleave to the pane.
  usePreventWindowFileDrop(() => setFileDragDepth(0));

  // Delete confirmation
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load ALL documents once; collection / uncategorised filtering is client-side
  // (fine at current list sizes) so we can show accurate sidebar counts and an
  // "Uncategorised" bucket without extra queries.
  const { documents, isLoading, isError } = useDocuments(
    undefined,
    showArchived,
  );
  const { deleteDocument, isLoading: isDeleting } = useDeleteDocument();
  const { collections } = useCollections();
  const { addToCollection, removeFromCollection } =
    useToggleDocumentCollection();
  const { addDocuments: bulkAddDocuments, isAdding: isBulkAdding } =
    useBulkAddToCollection();

  // Multi-select for bulk collection assignment (#157, #181)
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  // Last row clicked without shift; shift-click selects the visible range
  // between it and the clicked row. Stored as an id, not an index, so it
  // survives a re-sort and the range always means "what's between them now".
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);

  // A changed view can hide selected rows; a selection the user can't see
  // must not silently receive a bulk action.
  useEffect(() => {
    setSelectedDocIds(new Set());
    setSelectionAnchor(null);
  }, [selectedId, searchQuery, showArchived]);

  const toggleDocSelected = (id: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setSelected = (id: string) => {
    const next = Object.fromEntries(searchParams.entries());
    if (id === "all") delete next.collection;
    else next.collection = id;
    setSearchParams(next);
  };

  const uncategorisedCount = useMemo(
    () =>
      documents.filter((d) => !d.collections || d.collections.length === 0)
        .length,
    [documents],
  );

  const displayedDocuments = useMemo(() => {
    let list = documents;
    if (selectedId === UNCATEGORISED) {
      list = list.filter((d) => !d.collections || d.collections.length === 0);
    } else if (selectedId !== "all") {
      list = list.filter((d) =>
        (d.collections || []).some((c) => c.id === selectedId),
      );
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) list = list.filter((d) => d.filename.toLowerCase().includes(q));
    const cmp = SORT_COMPARATORS[sortKey];
    list = [...list].sort((a, b) => {
      const c = cmp(a, b);
      // Stable, predictable tie-break so equal statuses/sizes don't jitter
      return (sortDir === "asc" ? c : -c) || compareName(a, b);
    });
    return list;
  }, [documents, selectedId, searchQuery, sortKey, sortDir]);

  const totalSize = useMemo(
    () => displayedDocuments.reduce((sum, d) => sum + (d.sizeBytes || 0), 0),
    [displayedDocuments],
  );

  const activeCollection = collections.find((c) => c.id === selectedId);

  // Uploading (button or file drop) from a collection view pre-selects it
  const uploadPath =
    selectedId !== "all" && selectedId !== UNCATEGORISED
      ? `/documents/upload?collection=${selectedId}`
      : "/documents/upload";

  const handleFileDrop = (e: React.DragEvent) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    setFileDragDepth(0);
    // Only `drop` reliably populates files; the guard alone isn't proof
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      stashDroppedFiles(files);
      navigate(uploadPath);
    }
  };
  const breadcrumbName =
    selectedId === "all"
      ? "All documents"
      : selectedId === UNCATEGORISED
        ? "Uncategorised"
        : activeCollection?.name || "Collection";

  const allDisplayedSelected =
    displayedDocuments.length > 0 &&
    displayedDocuments.every((d) => selectedDocIds.has(d.id));

  const handleRowCheckboxClick = (
    e: React.MouseEvent<HTMLInputElement>,
    index: number,
  ) => {
    const doc = displayedDocuments[index];
    if (!doc) return;
    if (e.shiftKey && selectionAnchor != null) {
      const anchorIndex = displayedDocuments.findIndex(
        (d) => d.id === selectionAnchor,
      );
      if (anchorIndex !== -1) {
        const [from, to] =
          anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
        setSelectedDocIds((prev) => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) next.add(displayedDocuments[i].id);
          return next;
        });
        // Anchor stays put so a further shift-click extends from the same row
        return;
      }
    }
    toggleDocSelected(doc.id);
    setSelectionAnchor(doc.id);
  };

  // Used by both the toolbar select (current selection) and a multi-row drag.
  // No Undo: that would need a bulk-remove mutation the API doesn't have yet.
  const handleBulkAdd = async (collectionId: string, docIds: string[]) => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col || docIds.length === 0) return;
    try {
      const result = await bulkAddDocuments(collectionId, docIds);
      toast.success(
        `Added ${result.added} document${result.added !== 1 ? "s" : ""} to ${col.name}` +
          (result.notAdded > 0 ? ` (${result.notAdded} already there)` : ""),
      );
      setSelectedDocIds(new Set());
      setSelectionAnchor(null);
    } catch {
      toast.error("Failed to add documents to collection");
    }
  };

  // Adding/removing shows a confirmation toast with Undo so the action is
  // obvious in every view (in "All documents" the row itself doesn't move).
  const addWithToast = (collectionId: string, docId: string) => {
    const doc = documents.find((d) => d.id === docId);
    const col = collections.find((c) => c.id === collectionId);
    if (!doc || !col) return;
    if ((doc.collections || []).some((c) => c.id === collectionId)) {
      notify.info(`Already in ${col.name}`);
      return;
    }
    addToCollection(collectionId, docId);
    toast.success(
      (t) => (
        <span className="flex items-center gap-3">
          <span>
            Added to <span className="font-semibold">{col.name}</span>
          </span>
          <button
            onClick={() => {
              removeFromCollection(col.id, doc.id);
              toast.dismiss(t.id);
            }}
            className="font-semibold text-[var(--accent)] hover:underline"
          >
            Undo
          </button>
        </span>
      ),
      { duration: 5000 },
    );
  };

  const removeWithToast = (collectionId: string, docId: string) => {
    const doc = documents.find((d) => d.id === docId);
    const col = collections.find((c) => c.id === collectionId);
    if (!doc || !col) return;
    removeFromCollection(collectionId, docId);
    toast.success(
      (t) => (
        <span className="flex items-center gap-3">
          <span>
            Removed from <span className="font-semibold">{col.name}</span>
          </span>
          <button
            onClick={() => {
              addToCollection(col.id, doc.id);
              toast.dismiss(t.id);
            }}
            className="font-semibold text-[var(--accent)] hover:underline"
          >
            Undo
          </button>
        </span>
      ),
      { duration: 5000 },
    );
  };

  const handleDrop = (collectionId: string) => {
    const ids = draggedDocIds ? Array.from(draggedDocIds) : [];
    setDraggedDocIds(null);
    setDragOverColId(null);
    if (ids.length === 1) addWithToast(collectionId, ids[0]);
    else if (ids.length > 1) void handleBulkAdd(collectionId, ids);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    const result = await deleteDocument(documentToDelete.id);
    if (result.success) {
      setDocumentToDelete(null);
    } else {
      setDeleteError(result.error || "Failed to delete document");
    }
  };

  // ---- Collections sidebar item ----
  const CollectionRow = ({
    id,
    label,
    count,
    icon,
    muted = false,
    droppable = false,
    color,
  }: {
    id: string;
    label: string;
    count: number;
    icon: React.ReactNode;
    muted?: boolean;
    droppable?: boolean;
    color?: string | null;
  }) => {
    const active = selectedId === id;
    const isDropTarget = droppable && draggedDocIds != null;
    const dragCount = draggedDocIds?.size ?? 0;
    const isOver = isDropTarget && dragOverColId === id;
    return (
      <div
        onClick={() => setSelected(id)}
        onDragOver={
          droppable
            ? (e) => {
                e.preventDefault();
                setDragOverColId(id);
              }
            : undefined
        }
        onDragLeave={
          droppable
            ? () => setDragOverColId((cur) => (cur === id ? null : cur))
            : undefined
        }
        onDrop={droppable ? () => handleDrop(id) : undefined}
        className={`flex cursor-pointer items-center gap-[9px] rounded-[9px] px-[10px] py-2 text-[13.5px] transition-colors mb-px ${
          active
            ? "bg-[var(--accent-tint)] font-semibold text-[var(--accent-strong)]"
            : isOver
              ? "border-[1.5px] border-dashed border-[var(--accent-border)] bg-[var(--accent-tint-2)] text-[var(--text-2)]"
              : muted
                ? "text-[var(--text-3)] hover:bg-[var(--surface-2)]"
                : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
        }`}
      >
        {color ? (
          <span
            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: color }}
          />
        ) : (
          <span className="flex-shrink-0">{icon}</span>
        )}
        <span className="truncate">{label}</span>
        {isOver ? (
          <span className="ml-auto text-[11px] font-semibold text-[var(--accent)]">
            {dragCount > 1 ? `Drop ${dragCount} here` : "Drop here"}
          </span>
        ) : (
          <span
            className={`ml-auto font-mono text-[11px] ${
              active ? "text-[var(--accent)]" : "text-[var(--text-faint)]"
            }`}
          >
            {count}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col px-6 py-5">
      {/* Header */}
      <div className="mb-[18px] flex items-end justify-between gap-4">
        <div>
          <h1 className="mb-[5px] font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
            Documents
          </h1>
          <div className="font-mono text-[12.5px] text-[var(--text-3)]">
            {documents.length} files ·{" "}
            {formatTotalSize(
              documents.reduce((s, d) => s + (d.sizeBytes || 0), 0),
            )}{" "}
            · {collections.length} collections
          </div>
        </div>
        <button
          onClick={() => navigate(uploadPath)}
          className="inline-flex h-[38px] items-center gap-2 rounded-[9px] bg-[var(--accent)] px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-[var(--accent-strong)]"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Upload
        </button>
      </div>

      {/* Two-pane */}
      <div className="flex min-h-0 flex-1">
        {/* Collections sidebar */}
        <div
          className="mr-4 overflow-hidden transition-all duration-200"
          style={{
            width: colsOpen ? 264 : 0,
            marginRight: colsOpen ? 16 : 0,
            opacity: colsOpen ? 1 : 0,
          }}
        >
          <div className="flex h-full w-[264px] flex-col overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-between py-[10px] pl-[14px] pr-[10px]">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-3)]">
                Collections
              </span>
              <span className="flex items-center gap-0.5">
                <button
                  title="New collection"
                  onClick={() => setCreateModalOpen(true)}
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-[var(--accent)] hover:bg-[var(--surface-2)]"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
                <button
                  title="Collapse panel"
                  onClick={() => setColsOpen(false)}
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-[var(--text-3)] hover:bg-[var(--surface-2)]"
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </button>
              </span>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              <CollectionRow
                id="all"
                label="All documents"
                count={documents.length}
                icon={
                  <ListBulletIcon className="h-4 w-4 text-[var(--text-3)]" />
                }
              />
              {collections.map((c) => (
                <CollectionRow
                  key={c.id}
                  id={c.id}
                  label={c.name}
                  count={c.documentCount}
                  color={c.color}
                  droppable
                  icon={<FolderIcon className="h-4 w-4 text-[var(--text-3)]" />}
                />
              ))}
              <CollectionRow
                id={UNCATEGORISED}
                label="Uncategorised"
                count={uncategorisedCount}
                muted
                icon={
                  <FolderIcon className="h-4 w-4 text-[var(--text-faint)]" />
                }
              />
            </div>
          </div>
        </div>

        {/* Files pane — also a dropzone for OS files (internal doc-row drags
            are filtered out by isFileDragEvent and behave as before) */}
        <div
          onDragEnter={(e) => {
            if (!isFileDragEvent(e)) return;
            e.preventDefault();
            setFileDragDepth((d) => d + 1);
          }}
          onDragOver={(e) => {
            if (isFileDragEvent(e)) e.preventDefault();
          }}
          onDragLeave={(e) => {
            if (!isFileDragEvent(e)) return;
            setFileDragDepth((d) => Math.max(0, d - 1));
          }}
          onDrop={handleFileDrop}
          className={`relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-[12px] border bg-[var(--surface)] ${
            fileDragActive
              ? "border-[var(--accent)] ring-2 ring-[var(--accent)]"
              : "border-[var(--border)]"
          }`}
        >
          {fileDragActive && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[var(--surface)]/80">
              <p className="rounded-[9px] border border-[var(--accent)] bg-[var(--surface)] px-4 py-2 text-[13px] font-medium text-[var(--accent)]">
                Drop files to upload
                {activeCollection ? ` to ${activeCollection.name}` : ""}
              </p>
            </div>
          )}
          {/* Toolbar */}
          <div className="flex items-center gap-3 border-b border-[var(--border-2)] px-4 py-[13px]">
            {!colsOpen && (
              <button
                title="Show collections"
                onClick={() => setColsOpen(true)}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <ViewColumnsIcon className="h-4 w-4" />
              </button>
            )}
            {selectedDocIds.size > 0 ? (
              /* Selection bar takes the title's place (Drive/Gmail pattern)
                 so it reads as "actions for these rows", not part of the
                 breadcrumb, and has room for a full-width select (#181) */
              <div
                role="toolbar"
                aria-label="Selected documents"
                className="flex min-w-0 items-center gap-3 whitespace-nowrap"
              >
                <span className="text-[13px] font-semibold text-[var(--accent)]">
                  {selectedDocIds.size} selected
                </span>
                {/* Headless UI menu styled like RowMenu / the header avatar
                    menu, instead of a native select (looks out of place
                    in the redesign and is an action picker, not a value) */}
                <Menu as="div" className="relative">
                  <Menu.Button
                    disabled={isBulkAdding}
                    className="flex h-[34px] min-w-[190px] items-center justify-between gap-2 rounded-[9px] border border-[var(--accent-border)] bg-[var(--surface)] px-3 text-[13px] font-medium text-[var(--text)] hover:bg-[var(--surface-2)] disabled:opacity-50"
                  >
                    {isBulkAdding ? "Adding…" : "Add to collection…"}
                    <ChevronDownIcon className="h-4 w-4 text-[var(--text-faint)]" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute left-0 z-30 mt-1 w-56 origin-top-left rounded-[10px] border border-[var(--border)] bg-[var(--surface)] py-1 text-[13px] shadow-xl focus:outline-none">
                      {collections.length === 0 ? (
                        <div className="px-3 py-2 text-[var(--text-3)]">
                          No collections yet
                        </div>
                      ) : (
                        collections.map((c) => (
                          <Menu.Item key={c.id}>
                            {({ active }) => (
                              <button
                                onClick={() =>
                                  void handleBulkAdd(
                                    c.id,
                                    Array.from(selectedDocIds),
                                  )
                                }
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[var(--text-2)] ${
                                  active ? "bg-[var(--surface-2)]" : ""
                                }`}
                              >
                                <span
                                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                  style={{
                                    backgroundColor: c.color || "var(--accent)",
                                  }}
                                />
                                <span className="truncate">{c.name}</span>
                              </button>
                            )}
                          </Menu.Item>
                        ))
                      )}
                    </Menu.Items>
                  </Transition>
                </Menu>
                <button
                  onClick={() => {
                    setSelectedDocIds(new Set());
                    setSelectionAnchor(null);
                  }}
                  className="text-[12.5px] text-[var(--text-3)] hover:text-[var(--text)]"
                >
                  Clear
                </button>
              </div>
            ) : (
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-[var(--text)]">
                  {breadcrumbName}
                </div>
                <div className="mt-0.5 whitespace-nowrap font-mono text-[11.5px] text-[var(--text-faint)]">
                  {displayedDocuments.length} files ·{" "}
                  {formatTotalSize(totalSize)}
                </div>
              </div>
            )}
            <div className="flex-1" />
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-faint)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search this collection…"
                aria-label="Search documents by filename"
                className="h-[34px] w-44 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] pl-9 pr-3 text-[13px] text-[var(--text-2)] focus:w-56 focus:ring-2 focus:ring-accent focus:border-accent"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-[12.5px] text-[var(--text-3)]">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-[var(--border-strong)] text-[var(--accent)]"
              />
              Archived
            </label>
          </div>

          {/* Column header */}
          <div
            className={`${ROW_GRID} border-b border-[var(--border-2)] px-4 py-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--text-faint)]`}
          >
            <span className="flex items-center">
              <input
                type="checkbox"
                aria-label="Select all displayed documents"
                ref={(el) => {
                  if (el) {
                    el.indeterminate =
                      selectedDocIds.size > 0 && !allDisplayedSelected;
                  }
                }}
                checked={allDisplayedSelected}
                onChange={() => {
                  setSelectedDocIds(
                    allDisplayedSelected
                      ? new Set()
                      : new Set(displayedDocuments.map((d) => d.id)),
                  );
                  setSelectionAnchor(null);
                }}
                className="cursor-pointer rounded border-[var(--border-strong)] text-[var(--accent)]"
              />
            </span>
            <span />
            {SORT_COLUMNS.map(([key, label]) => {
              const active = sortKey === key;
              return (
                <span
                  key={key}
                  role="columnheader"
                  aria-sort={
                    active
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(key)}
                    title={`Sort by ${label.toLowerCase()}`}
                    aria-label={`Sort by ${label.toLowerCase()}${
                      active
                        ? `, ${sortDir === "asc" ? "ascending" : "descending"}`
                        : ""
                    }`}
                    className={`group/sort inline-flex items-center gap-1 uppercase tracking-[0.06em] hover:text-[var(--text-2)] ${
                      active ? "text-[var(--text-2)]" : ""
                    }`}
                  >
                    {label}
                    <span
                      aria-hidden="true"
                      className={`font-mono normal-case tracking-normal ${
                        active ? "" : "opacity-0 group-hover/sort:opacity-60"
                      }`}
                    >
                      {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                </span>
              );
            })}
            <span />
          </div>

          {/* Rows */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-10 text-center text-[13px] text-[var(--text-3)]">
                Loading documents…
              </div>
            ) : isError ? (
              <div className="m-4 rounded-[12px] bg-[var(--err-bg)] p-4 text-[13px] text-[var(--err-text)]">
                Failed to load documents.
              </div>
            ) : displayedDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                <DocumentTextIcon className="h-10 w-10 text-[var(--text-faint)]" />
                <p className="text-[13px] text-[var(--text-3)]">
                  {searchQuery.trim()
                    ? `No documents match “${searchQuery.trim()}”.`
                    : selectedId === "all"
                      ? "No documents yet. Upload your first document to get started."
                      : "No documents in this collection yet — drag files here to add them."}
                </p>
              </div>
            ) : (
              displayedDocuments.map((doc, index) => (
                <div
                  key={doc.id}
                  // Drags start from the row itself (the grip is only an
                  // affordance). Finder rule: a ticked row drags the whole
                  // selection, an unticked row drags just itself.
                  draggable
                  onDragStart={(e) => {
                    const ids = selectedDocIds.has(doc.id)
                      ? new Set(selectedDocIds)
                      : new Set([doc.id]);
                    setDraggedDocIds(ids);
                    e.dataTransfer.effectAllowed = "move";
                    // Firefox won't start a drag without a payload
                    e.dataTransfer.setData(
                      "text/plain",
                      Array.from(ids).join(","),
                    );
                  }}
                  onDragEnd={() => {
                    setDraggedDocIds(null);
                    setDragOverColId(null);
                  }}
                  onClick={() => navigate(`/documents/${doc.id}`)}
                  className={`${ROW_GRID} group relative cursor-pointer border-b border-[var(--border-2)] px-4 py-[11px] transition-colors hover:bg-[var(--surface-2)] ${
                    draggedDocIds?.has(doc.id) ? "opacity-40" : ""
                  }`}
                >
                  {/* Hover-only grip lives in the row's left padding so the
                      checkbox column stays put and always visible (#181) */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 top-1/2 hidden -translate-y-1/2 group-hover:block"
                  >
                    <GripIcon />
                  </span>
                  <span
                    className="flex items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${doc.filename}`}
                      checked={selectedDocIds.has(doc.id)}
                      onChange={noop}
                      onClick={(e) => handleRowCheckboxClick(e, index)}
                      // Shift-click would otherwise extend a text selection
                      onMouseDown={(e) => {
                        if (e.shiftKey) e.preventDefault();
                      }}
                      className="cursor-pointer rounded border-[var(--border-strong)] text-[var(--accent)]"
                    />
                  </span>
                  <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[var(--file-bg)]">
                    <DocumentTextIcon className="h-4 w-4 text-[var(--file-fg)]" />
                  </span>
                  <div className="min-w-0 pr-4">
                    <MiddleTruncate
                      text={doc.filename}
                      className="text-[14.5px] font-medium text-[var(--text)]"
                    />
                    {doc.collections && doc.collections.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 overflow-hidden">
                        {doc.collections.slice(0, 3).map((c) => (
                          <span
                            key={c.id}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-[6px] border border-[var(--border-2)] bg-[var(--surface-2)] px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--text-3)]"
                          >
                            <span
                              className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                              style={{
                                backgroundColor: c.color || "var(--accent)",
                              }}
                            />
                            {c.name}
                          </span>
                        ))}
                        {doc.collections.length > 3 && (
                          <span className="text-[10.5px] text-[var(--text-faint)]">
                            +{doc.collections.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <StatusPill status={doc.status} />
                  <span className="font-mono text-[12.5px] text-[var(--text-2)]">
                    {formatFileSize(doc.sizeBytes)}
                  </span>
                  <span className="font-mono text-[12.5px] text-[var(--text-3)]">
                    {format(new Date(doc.createdAt), "MMM d")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuDocId((cur) => (cur === doc.id ? null : doc.id));
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--text-faint)] hover:bg-[var(--surface-2)] hover:text-[var(--text-2)]"
                  >
                    <EllipsisHorizontalIcon className="h-[18px] w-[18px]" />
                  </button>
                  {menuDocId === doc.id && (
                    <RowMenu
                      doc={doc}
                      collections={collections}
                      onOpen={() => {
                        setMenuDocId(null);
                        navigate(`/documents/${doc.id}`);
                      }}
                      onAdd={(cid) => {
                        addWithToast(cid, doc.id);
                        setMenuDocId(null);
                      }}
                      onRemove={(cid) => {
                        removeWithToast(cid, doc.id);
                        setMenuDocId(null);
                      }}
                      onDelete={() => {
                        setMenuDocId(null);
                        setDeleteError(null);
                        setDocumentToDelete(doc);
                      }}
                      onClose={() => setMenuDocId(null)}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create collection modal */}
      {createModalOpen && (
        <CreateCollectionModal onClose={() => setCreateModalOpen(false)} />
      )}

      {/* Delete confirmation dialog */}
      {documentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => !isDeleting && setDocumentToDelete(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-6 shadow-xl">
              <div className="mb-4 flex items-center gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[var(--err-bg)]">
                  <ExclamationTriangleIcon className="h-6 w-6 text-[var(--err-text)]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text)]">
                    Delete Document
                  </h3>
                  <p className="text-sm text-[var(--text-faint)]">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <p className="mb-4 text-sm text-[var(--text-2)]">
                Are you sure you want to delete{" "}
                <span className="font-medium text-[var(--text)]">
                  {documentToDelete.filename}
                </span>
                ? This will permanently remove the document and all its
                processed chunks from the system.
              </p>
              {deleteError && (
                <div className="mb-4 rounded-lg border border-[var(--err-text)] bg-[var(--err-bg)] p-3">
                  <p className="text-sm text-[var(--err-text)]">
                    {deleteError}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDocumentToDelete(null)}
                  disabled={isDeleting}
                  className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-4 py-2 text-sm font-medium text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
